import { makeFriendCircle } from '../lib/ab/friends';
import { ABScreen } from '../lib/ab/screen';
import { makeCitySegmented, makeGenreToggles } from '../lib/ab/widgets-home';
import { aTrackPage } from '../lib/mdl/a-track';
import { c_cities } from '../lib/mdl/c-city';
import { meUpdateEventTags } from '../lib/me/home';
import { the_user } from '../lib/me/login';
import { meChangePassword, meUpdate } from '../lib/me/me';
import { UIButton } from '../lib/ui/component/button';
import { UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UIAutofill } from '../lib/ui/component/input-base';
import { UISegmented } from '../lib/ui/component/segmented';
import { uiToast } from '../lib/ui/component/toast';
import { UISwitch } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { uiMakeI18NValues } from '../lib/ui/functions';
import { UIValue } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { $analytics } from '../lib/y/analytics';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { $i18n, YLanguage, the_language, ySetLanguage } from '../lib/y/i18n';
import { $location } from '../lib/y/location';
import { HomeMenu } from './menu';

export class HomeProfileScreen extends ABScreen {
    constructor() {
        super({ menu: new HomeMenu({ classes: 'm-account' }) });
    }

    protected override fillContent(content: UIViewSpec): void {
        const first_name = new UITextInput({ placeholder: $i18n.tl.ph_first_name, disabled: true });
        const last_name = new UITextInput({ placeholder: $i18n.tl.ph_last_name, disabled: true });
        const email = new UITextInput({ type: 'email', placeholder: $i18n.tl.ph_email, required: true });
        const phone = new UITextInput({ type: 'phone', placeholder: $i18n.tl.ph_phone, required: true });
        const gender_values = uiMakeI18NValues($i18n.tl.o_gender, ['female', 'male', 'other']);
        const gender = new UISegmented(gender_values, { classes: 'm-light-border m-flexible ui-tint-reverse' });
        const birth_year = new UINumberInput({ placeholder: $i18n.tl.ph_birth_year })
        const save = new UIButton({
            title: $i18n.tl.b_save, on_click: () => {
                if(!form.validate()) return;
                return meUpdate(form.value).then($fn.TRUE, uiToast);
            }
        });

        const form = $form({ email, phone, gender, birth_year });

        form.value = the_user!;
        first_name.value = the_user!.first_name;
        last_name.value = the_user!.last_name;

        //for autofill purposes only
        const username = new UITextInput({ type: 'email', autofill: UIAutofill.USERNAME });
        username.value = the_user!.email ?? '';
        username.ui_hide();

        const old_password = new UITextInput({ type: 'password', placeholder: $i18n.tl.ph_current_pw, autofill: UIAutofill.OLD_PASSWORD, required: true });
        const new_password = new UITextInput({ type: 'password', placeholder: $i18n.tl.ph_new_pw, autofill: UIAutofill.NEW_PASSWORD, required: true });
        const change = new UIButton({
            title: $i18n.tl.b_change_pw, on_click: () => {
                if(!$form.validate([old_password, new_password])) return;
                return meChangePassword(old_password.value, new_password.value).then($fn.TRUE, uiToast);
            }
        });

        const bottom_left = $el.DIV('l-form', [
            $el.H3('heading', $i18n.tl.h_friends)
        ]);

        const show_activity = new UISwitch({ classes: 'ui-tint-reverse' });
        show_activity.value = !the_user!.flags.hide_activity;
        $on(show_activity, 'value', v => {
            the_user!.flags.hide_activity = v ? undefined : true;
            meUpdate({ flags: { hide_activity: !v } });
        });

        bottom_left.ui_add([
            $el.P('input-hint', $i18n.tl.t_profile_show_activity),
            $el.DIV('l-input-row', [$i18n.tl.cb_profile_show_activity, $el.S(), show_activity])
        ]);

        const avatar = makeFriendCircle(the_user!.first_name, the_user!.last_name, the_user!.avatar_id);
        avatar.classList.add('m-profile');

        const tags = makeGenreToggles();
        tags.value = the_user!.event_tag_ids;
        // TODO: add throttling
        $on(tags, 'value', meUpdateEventTags);

        const language_values: UIValue<YLanguage>[] = [{ key: 'sv', name: 'Svenska' }, { key: 'en', name: 'English' }, { key: 'de', name: 'Deutsch' }];
        const language = new UISegmented(language_values, { classes: 'm-light-border m-flexible ui-tint-reverse' });
        language.value = the_user!.language;
        $on(language, 'value', v => {
            $analytics.track('Change language', { new_language: v });
            meUpdate({ language: v }).then(() => {
                if(the_user!.language != the_language) {
                    ySetLanguage(the_user!.language);
                    $location.reload();
                }
            });
        });

        const city = makeCitySegmented({ classes: 'm-light-border m-flexible ui-tint-reverse' });
        city.value = the_user!.city_id;
        $on(city, 'value', v => {
            $analytics.track('Change city', { new_city: c_cities.maybe(v!)!.name });
            meUpdate({ city_id: v });
        });

        content.ui_add($el.DIV('ui-section l-columns w-block', [
            $el.DIV('i-column m-540', [
                $el.FORM('l-form', [
                    $el.H3('heading', $i18n.tl.h_profile),
                    $el.P('input-hint', $i18n.tl.t_profile_name),
                    first_name,
                    last_name,
                    email,
                    birth_year,
                    phone,
                    gender,
                    $el.DIV('l-button-row m-left', save),
                    $el.DIV('s-medium'),
                    $el.H3('heading', $i18n.tl.h_profile_pw), username, old_password, new_password,
                    $el.DIV('l-button-row m-left', change),
                ]),
                $el.DIV('s-medium'),
                bottom_left
            ]),
            $el.DIV('i-column m-540', [
                $el.DIV('l-form', [
                    avatar,
                    $el.DIV('s-medium'),
                    $el.H3('heading', $i18n.tl.h_profile_favorites),
                    $el.P('input-hint', $i18n.tl.t_profile_favorites),
                    tags,
                    $el.DIV('s-medium'),
                    $el.H3('heading', $i18n.tl.h_city),
                    city,
                    $el.DIV('s-medium'),
                    $el.H3('heading', $i18n.tl.h_language),
                    language,
                    xavis_experiments && new UIButton({
                        title: 'Notifications', on_click: () => {
                            if(Notification.permission == 'granted') {
                                $log('app', 'We already have permission');
                            } else if(Notification.permission == 'denied') {
                                $log('app', 'Already denied');
                            } else {
                                Notification.requestPermission().then(permission => {
                                    $log('app', 'Got permission: ' + permission);
                                });
                            }
                        }
                    })
                ])
                //download data experiment
                // $el.A({ href: '/docs/user/'+the_user!.id+'/info?token='+$tl.the_login!.alt_token, download: 'Abundo-Data.json', here: true },
                //                               'Download all my data (in JSON format)', 'r-download-info')
            ])
        ]));

        aTrackPage('account');
    }
}
